import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Download, Printer, AlertCircle, Crown } from 'lucide-react';
import { exportInvoiceToPDF } from '@/lib/export';
import { InvoicePrintView } from '@/components/invoice/InvoicePrintView';
import type { Invoice } from '@/types/invoice';
import { SEO } from '@/components/SEO';
import { useAuth } from '@/hooks/useAuth';
import { Logo } from '@/components/ui/Logo';

export default function SharedInvoice() {
  const { userId, invoiceId } = useParams<{ userId: string; invoiceId: string }>();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSharedInvoice = async () => {
      if (!userId || !invoiceId) {
        setError('Invalid URL link details.');
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, 'users', userId, 'invoices', invoiceId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setInvoice({
            ...data,
            invoiceId: docSnap.id,
            issueDate: data.issueDate?.toDate(),
            dueDate: data.dueDate?.toDate(),
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
          } as Invoice);
        } else {
          setError('The requested invoice was not found.');
        }
      } catch (err: any) {
        console.error('Error fetching shared invoice:', err);
        if (err.code === 'permission-denied') {
          setError('This document is not shared, or the owner does not have sharing enabled.');
        } else {
          setError('Failed to load shared invoice.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSharedInvoice();
  }, [userId, invoiceId]);

  useEffect(() => {
    if (authLoading) return;
    if (!invoice || !userId || !invoiceId) return;

    // Do not log view if the viewer is the creator/owner of the invoice
    if (user && user.uid === userId) {
      console.log('Bypassing view logging since the viewer is the invoice creator.');
      return;
    }

    const logInvoiceView = async () => {
      try {
        const sessionKey = `invoice_viewed_${invoiceId}`;
        const hasViewedInSession = sessionStorage.getItem(sessionKey) === 'true';
        if (hasViewedInSession) return;

        // Log view to the subcollection
        const viewRef = collection(db, 'users', userId, 'invoices', invoiceId, 'views');
        await addDoc(viewRef, {
          viewedAt: new Date(),
          userAgent: navigator.userAgent,
          browser: getBrowserName(navigator.userAgent),
          os: getOSName(navigator.userAgent),
        });

        sessionStorage.setItem(sessionKey, 'true');
      } catch (err) {
        console.error('Failed to log invoice view:', err);
      }
    };

    logInvoiceView();
  }, [invoice, userId, invoiceId, user, authLoading]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!invoice) return;

    setDownloading(true);
    try {
      await exportInvoiceToPDF(invoice, 'invoice-content');
      toast({ title: 'Success', description: 'Invoice downloaded successfully!' });
    } catch (err) {
      console.error('Download error:', err);
      toast({
        title: 'Download Failed',
        description: 'Failed to download invoice PDF.',
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
        <Card className="max-w-md w-full shadow-lg border-red-100">
          <CardContent className="py-12 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 text-red-600">
              <AlertCircle className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-bold text-gray-950">Access Restricted</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {error || 'Unable to view this invoice.'}
            </p>
            <div className="pt-2 text-xs text-muted-foreground">
              Document direct links are a premium feature of QuickFreeInvoice Pro.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // useEffect was moved up to follow rules of hooks

  const businessInfo = invoice ? ((invoice as any).from || (invoice as any)._businessInfo || {}) : {};

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <SEO
        title={invoice ? `Invoice ${invoice.invoiceNumber || ''} - ${(invoice as any).from?.name || 'QuickFreeInvoice'}` : 'View Invoice - QuickFreeInvoice'}
        description={invoice ? `View invoice ${invoice.invoiceNumber || ''} from ${(invoice as any).from?.name || 'issuer'} for ${invoice.billTo?.name || 'Client'}.` : 'Secure client invoice preview.'}
        noindex={true}
      />
      {/* Sticky top action bar for client */}
      <div className="sticky top-0 bg-white border-b print:hidden z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo />
            <span className="text-xs text-muted-foreground bg-gray-100 px-2 py-0.5 rounded">Client View</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button size="sm" onClick={handleDownloadPDF} disabled={downloading}>
              <Download className="h-4 w-4 mr-2" />
              {downloading ? 'Downloading...' : 'Download PDF'}
            </Button>
          </div>
        </div>
      </div>

      {/* Render Document */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Payment CTA Banner */}
          {invoice && invoice.status !== 'paid' && (businessInfo.stripePaymentLink || businessInfo.paypalLink) && (
            <Card id="payment-banner" className="border-indigo-100 bg-gradient-to-r from-indigo-50/50 to-purple-50/30 overflow-hidden shadow-sm text-left">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                  <div>
                    <h4 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2">
                      <Crown className="h-5 w-5 text-amber-500 animate-pulse" />
                      Pay Invoice Online
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      The issuer has enabled direct online payments. Choose a payment method to complete the transaction.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 w-full md:w-auto">
                    {businessInfo.stripePaymentLink && (
                      <a
                        href={businessInfo.stripePaymentLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 md:flex-initial"
                      >
                        <Button className="w-full bg-[#635bff] hover:bg-[#5951e5] text-white font-semibold shadow-sm">
                          Pay with Stripe
                        </Button>
                      </a>
                    )}
                    {businessInfo.paypalLink && (
                      <a
                        href={businessInfo.paypalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 md:flex-initial"
                      >
                        <Button className="w-full bg-[#ffc439] hover:bg-[#e6b033] text-[#003087] font-semibold shadow-sm">
                          Pay with PayPal
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Security Advisory Disclaimer Banner */}
          <div className="bg-amber-50/80 border border-amber-200/80 rounded-lg p-4 text-left flex items-start gap-3 shadow-sm print:hidden">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <h5 className="font-heading font-semibold text-sm text-amber-900">Security Notice</h5>
              <p className="text-xs text-amber-800/90 mt-1 leading-relaxed">
                Always verify the identity of the sender and confirm invoice details directly with the issuer before making any payment. 
                QuickFreeInvoice is a document generation platform; we do not verify issuer credentials, collect payments directly, or assume responsibility for transactions.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto w-full">
            <div className="min-w-[600px] md:min-w-0">
              <InvoicePrintView invoice={invoice} elementId="invoice-content" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getBrowserName(ua: string) {
  if (ua.includes('Chrome') && !ua.includes('Chromium') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Edg')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  return 'Other Browser';
}

function getOSName(ua: string) {
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Macintosh') || ua.includes('Mac OS')) return 'macOS';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('Linux')) return 'Linux';
  return 'Other OS';
}
